import safeStorage from '@github-ui/safe-storage'
import {useCallback, useEffect, useRef, useState, useSyncExternalStore} from 'react'

export interface PrettifiedBlock {
  id: string
  code: string
  collapsed: boolean
}

const MAX_PERSISTED_BLOCKS_SIZE = 300_000
const NOT_LOADED = Symbol('not-loaded')
const submissionListeners = new Map<string, Set<(submitted: ReadonlyMap<string, string>) => void>>()

function subscribeNoop() {
  return () => {}
}

function blockStorage() {
  return safeStorage('localStorage', {throwQuotaErrorsOnSet: true, ttl: 1000 * 60 * 60 * 24})
}

function isPrettifiedBlock(value: unknown): value is PrettifiedBlock {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'string' &&
    value.id.length > 0 &&
    'code' in value &&
    typeof value.code === 'string' &&
    'collapsed' in value &&
    typeof value.collapsed === 'boolean'
  )
}

function readPersistedBlocks(draftKey: string | undefined): PrettifiedBlock[] {
  if (draftKey === undefined) return []
  const stored = blockStorage().getItem(`COPILOT_PRETTIFIED_BLOCKS_${draftKey}`)
  if (!stored || stored.length > MAX_PERSISTED_BLOCKS_SIZE) return []
  try {
    const parsed: unknown = JSON.parse(stored)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'v' in parsed &&
      parsed.v === 1 &&
      'blocks' in parsed &&
      Array.isArray(parsed.blocks) &&
      parsed.blocks.every(isPrettifiedBlock) &&
      new Set(parsed.blocks.map(block => block.id)).size === parsed.blocks.length
    ) {
      return parsed.blocks
    }
  } catch {
    return []
  }
  return []
}

function persistBlocks(draftKey: string, blocks: PrettifiedBlock[]) {
  const storage = blockStorage()
  const key = `COPILOT_PRETTIFIED_BLOCKS_${draftKey}`
  if (blocks.length === 0) {
    storage.removeItem(key)
    return
  }
  const serialized = JSON.stringify({v: 1, blocks})
  if (serialized.length > MAX_PERSISTED_BLOCKS_SIZE) {
    storage.removeItem(key)
    return
  }
  try {
    storage.setItem(key, serialized)
  } catch {
    storage.removeItem(key)
  }
}

function generateBlockId() {
  return `prettified-block-${crypto.randomUUID()}`
}

export function usePrettifiedBlocks(options?: {draftKey?: string; restore?: boolean}) {
  const draftKey = options?.draftKey
  const hydrated = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  )
  const [blocks, setBlocks] = useState<PrettifiedBlock[]>([])
  // ref kept in sync so getBlocksText() can read current state without stale closure issues
  const blocksRef = useRef<PrettifiedBlock[]>(blocks)
  const acceptedBlocksRef = useRef(new Map<string, string>())
  const [loadedKey, setLoadedKey] = useState<string | undefined | typeof NOT_LOADED>(NOT_LOADED)
  if (hydrated && loadedKey !== draftKey) {
    setLoadedKey(draftKey)
    if (loadedKey !== undefined || blocks.length === 0) {
      setBlocks(loadedKey === NOT_LOADED && options?.restore === false ? [] : readPersistedBlocks(draftKey))
    }
  }

  useEffect(() => {
    const currentBlocks = blocks.filter(block => acceptedBlocksRef.current.get(block.id) !== block.code)
    blocksRef.current = currentBlocks
    if (currentBlocks.length === blocks.length) acceptedBlocksRef.current.clear()
    if (hydrated && draftKey !== undefined && loadedKey === draftKey) persistBlocks(draftKey, currentBlocks)
  }, [hydrated, loadedKey, blocks, draftKey])

  useEffect(() => {
    return () => {
      blocksRef.current = []
    }
  }, [])

  const updateBlocks = useCallback((updater: (prev: PrettifiedBlock[]) => PrettifiedBlock[]) => {
    setBlocks(prev => {
      const next = updater(prev)
      blocksRef.current = next
      return next
    })
  }, [])

  const addBlock = useCallback(
    (code: string) => {
      const block: PrettifiedBlock = {id: generateBlockId(), code, collapsed: false}
      updateBlocks(prev => [...prev, block])
    },
    [updateBlocks],
  )

  const removeBlock = useCallback((id: string) => updateBlocks(prev => prev.filter(b => b.id !== id)), [updateBlocks])

  const applySubmittedBlocks = useCallback(
    (submitted: ReadonlyMap<string, string>) => {
      for (const [id, code] of submitted) acceptedBlocksRef.current.set(id, code)
      blocksRef.current = blocksRef.current.filter(block => submitted.get(block.id) !== block.code)
      updateBlocks(prev => prev.filter(b => submitted.get(b.id) !== b.code))
    },
    [updateBlocks],
  )

  useEffect(() => {
    if (draftKey === undefined) return
    const listeners = submissionListeners.get(draftKey) ?? new Set()
    submissionListeners.set(draftKey, listeners)
    listeners.add(applySubmittedBlocks)
    return () => {
      listeners.delete(applySubmittedBlocks)
      if (listeners.size === 0) submissionListeners.delete(draftKey)
    }
  }, [draftKey, applySubmittedBlocks])

  const removeSubmittedBlocks = useCallback(
    (submitted: ReadonlyMap<string, string>) => {
      if (draftKey !== undefined) {
        persistBlocks(
          draftKey,
          readPersistedBlocks(draftKey).filter(block => submitted.get(block.id) !== block.code),
        )
      }
      // Only remove a block if its current content still matches what was
      // submitted. A block the user edited while the submit was pending has
      // different content, so we keep it rather than silently discarding the
      // edit (its new content was never sent).
      applySubmittedBlocks(submitted)
      if (draftKey !== undefined) {
        for (const listener of submissionListeners.get(draftKey) ?? []) {
          if (listener !== applySubmittedBlocks) listener(submitted)
        }
      }
    },
    [draftKey, applySubmittedBlocks],
  )

  const toggleCollapse = useCallback(
    (id: string) => updateBlocks(prev => prev.map(b => (b.id === id ? {...b, collapsed: !b.collapsed} : b))),
    [updateBlocks],
  )

  const updateBlockCode = useCallback(
    (id: string, newCode: string) => updateBlocks(prev => prev.map(b => (b.id === id ? {...b, code: newCode} : b))),
    [updateBlocks],
  )

  const clearBlocks = useCallback(() => {
    blocksRef.current = []
    setBlocks([])
  }, [])

  /**
   * Returns the content of all prettified blocks as markdown fenced code blocks.
   * Reads from a ref so it is always current even inside async callbacks.
   */
  const getBlocksText = useCallback((): string => {
    return blocksRef.current
      .map(b => {
        // find longest consecutive backtick run in the code and use one longer for the fence
        const maxRun = (b.code.match(/`+/g) ?? []).reduce((max, m) => Math.max(max, m.length), 0)
        const fence = '`'.repeat(Math.max(3, maxRun + 1))
        return `${fence}\n${b.code}\n${fence}`
      })
      .join('\n')
  }, [])

  return {
    blocks,
    addBlock,
    removeBlock,
    removeSubmittedBlocks,
    updateBlockCode,
    toggleCollapse,
    clearBlocks,
    getBlocksText,
  }
}
